import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import ApiGrid from '@components-clean/core/api-grid';
import { GridHeaderTitle } from '@components-clean/core/grid/grid.interface';
import Loading from '@components-clean/core/loading/loading';
import { useCountrySelectorContext } from '@contexts-clean/country-selector-context';
import { CM2Dataset, ControlsHubOoTGridParams, ControlsHubPanelGridParams } from '@interfaces/central-monitoring';
import { useTranslation } from '@hooks/index';
import { GET_CONTROLS_HUB_CONFIG_PANELS_RESOLVED } from '@queries/central-monitoring';
import {
    getControlsHubConfigPanelsForStudyResolved,
    getControlsHubConfigPanelsForStudyResolvedVariables,
} from '@queries/generated/central-monitoring.generated';

type Props = {
    studyRef: string;
    dataset: CM2Dataset;
};

const OUT_OF_TOLERANCE_ID = '-1';
const PANEL_GRID_TABLE = 'controls_hub_panel_grid_data';
const OOT_GRID_TABLE = 'controls_hub_oot_data';

export const GridSection = ({ studyRef, dataset }: Props) => {
    const { t } = useTranslation();

    const { country } = useCountrySelectorContext();

    const [selectedGrid, setSelectedGrid] = useState<string | null>(OUT_OF_TOLERANCE_ID);

    const { data: controlsHubConfigPanels, loading: isLoading } = useQuery<
        getControlsHubConfigPanelsForStudyResolved,
        getControlsHubConfigPanelsForStudyResolvedVariables
    >(GET_CONTROLS_HUB_CONFIG_PANELS_RESOLVED, {
        variables: { studyId: studyRef, timestamp: dataset.created },
    });

    useEffect(() => {
        // Reset the grid to out of tolerance on dataset change. It's possible the current selected panel doesn't
        // exist on controls hub for the new dataset
        setSelectedGrid(OUT_OF_TOLERANCE_ID);
    }, [dataset.id]);

    const panels = controlsHubConfigPanels?.getControlsHubConfigPanelsResolvedForStudy?.panels;
    const titleDatasource = useMemo(() => {
        const titles: GridHeaderTitle[] =
            panels?.map((x) => ({
                id: x?.panelRef,
                label: x?.plotTitle,
                active: x?.active,
            })) ?? [];

        titles.unshift({
            id: OUT_OF_TOLERANCE_ID,
            label: t('cm:controlsHub.grid.outOfTolerance'),
        });

        return titles;
    }, [panels]);

    const table = selectedGrid === OUT_OF_TOLERANCE_ID ? OOT_GRID_TABLE : PANEL_GRID_TABLE;

    // Depending on how country filter is implemented, this might be easier to maintain as state not memo
    const gridParams = useMemo((): ControlsHubPanelGridParams | ControlsHubOoTGridParams => {
        if (selectedGrid === OUT_OF_TOLERANCE_ID || selectedGrid == null) {
            return {
                grid: 'ControlsHubOoTGrid',
                studyRef,
                country: country.value,
                isChild: false,
            };
        }

        return {
            grid: 'ControlsHubPanelGrid',
            studyRef,
            datasetId: dataset.id!,
            panelRef: selectedGrid,
            country: country.value,
            isChild: false,
        };
    }, [selectedGrid, country]);

    if (isLoading) return <Loading />;

    return (
        <ApiGrid
            key={selectedGrid}
            testDataId="controls-hub-grid"
            title={titleDatasource}
            titleReadOnly={titleDatasource.length === 1}
            selectedTitle={titleDatasource.find((x) => x.id === selectedGrid)}
            onTitleChange={(e) => {
                if (e.value) setSelectedGrid((e.value as GridHeaderTitle).id as string);
            }}
            titleWidth={420}
            maxHeight="auto"
            table={table}
            gridParams={gridParams}
            filterable
            scrollable="virtual"
            dataItemKey="id"
            noPadding
            showAddIcon={false}
        />
    );
};
